import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { XCircle } from 'lucide-react';

const MOTIVOS_INATIVACAO = [
  { value: 'inadimplencia', label: 'Inadimplência' },
  { value: 'vencimento', label: 'Vencimento do Contrato' },
  { value: 'rescisao_cliente', label: 'Rescisão pelo Cliente' },
  { value: 'rescisao_empresa', label: 'Rescisão pela Empresa' },
  { value: 'descumprimento', label: 'Descumprimento Contratual' },
  { value: 'encerramento_atividades', label: 'Encerramento de Atividades do Cliente' },
  { value: 'outro', label: 'Outro Motivo' }
];

export default function InativarContratoDialog({ open, onClose, contrato, onConfirm }) {
  const [motivoSelecionado, setMotivoSelecionado] = useState('');
  const [observacao, setObservacao] = useState('');
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    if (!motivoSelecionado) return;
    
    const motivoLabel = MOTIVOS_INATIVACAO.find(m => m.value === motivoSelecionado)?.label || motivoSelecionado;
    const motivoFinal = observacao ? `${motivoLabel} - ${observacao}` : motivoLabel;
    
    setLoading(true);
    await onConfirm(contrato, motivoFinal);
    setLoading(false);
    setMotivoSelecionado('');
    setObservacao('');
    onClose();
  };

  const handleClose = () => {
    setMotivoSelecionado('');
    setObservacao('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="dialog-inativar-contrato">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <XCircle className="w-5 h-5" />
            Inativar Contrato
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {contrato && (
            <div className="p-3 bg-slate-50 rounded-lg text-sm">
              <p><strong>Contrato:</strong> {contrato.numero_contrato}</p>
              <p><strong>Cliente:</strong> {contrato.cliente_nome}</p>
            </div>
          )}

          <div className="space-y-2">
            <Label>Motivo da Inativação *</Label>
            <Select value={motivoSelecionado} onValueChange={setMotivoSelecionado}>
              <SelectTrigger data-testid="select-motivo-inativacao">
                <SelectValue placeholder="Selecione o motivo..." />
              </SelectTrigger>
              <SelectContent>
                {MOTIVOS_INATIVACAO.map(m => (
                  <SelectItem key={m.value} value={m.value} data-testid={`motivo-${m.value}`}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {motivoSelecionado === 'outro' && (
            <div className="space-y-2">
              <Label>Especifique o motivo</Label>
              <Textarea
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                placeholder="Descreva o motivo da inativação..."
                data-testid="input-observacao-inativacao"
              />
            </div>
          )}

          {motivoSelecionado && motivoSelecionado !== 'outro' && (
            <div className="space-y-2">
              <Label>Observação adicional (opcional)</Label>
              <Textarea
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                placeholder="Adicione detalhes se necessário..."
                data-testid="input-observacao-inativacao"
              />
            </div>
          )}

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <p className="font-semibold">Atenção:</p>
            <p>Ao inativar, a recepção de materiais deste cliente será bloqueada e o cliente será notificado via WhatsApp e Email.</p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} data-testid="btn-cancelar-inativacao">
            Cancelar
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleConfirm} 
            disabled={!motivoSelecionado || (motivoSelecionado === 'outro' && !observacao) || loading}
            data-testid="btn-confirmar-inativacao"
          >
            {loading ? 'Inativando...' : 'Confirmar Inativação'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
